'use client';

import { useMicrocopy } from '@contexts/MicrocopyContext';
import { MicrocopyFragment } from '@graphql/generated-contentful/graphql';
import { useController } from '@lib/hooks/useController';
import { useCallback, useMemo } from 'react';
import { CheckboxWithLabel } from 'ui-components';
import { useEngine } from '../EngineContext';
import { FacetAccordion } from '../FacetAccordion';
import { FacetDropdown } from '../FacetDropdown';
import { VERSION_RANGES, getUnityVersionController } from '../facetControllers';
import { useOptimisticFacetSelection } from '../hooks/useOptimisticFacetSelection';
import { useStableFacetValues } from '../hooks/useStableFacetValues';
import { UnityVersionFacetProps } from './UnityVersionFacet.types';

export const UnityVersionFacet = ({
  variant = 'horizontal',
  className,
  isOpen,
  onToggle,
}: UnityVersionFacetProps) => {
  const { searchLabelsMicrocopy } = useMicrocopy<{
    searchLabelsMicrocopy: MicrocopyFragment[];
  }>();

  const versionLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.unity-version.title')
      ?.value ?? 'Unity Version';

  const clearLabel =
    searchLabelsMicrocopy?.find((m) => m.key === 'search.unity-version.clear')
      ?.value ?? 'Clear';

  const eng = useEngine();
  const controller = useMemo(() => getUnityVersionController(eng), [eng]);
  const state = useController(controller);
  const stableValues = useStableFacetValues(
    state.values,
    'min_unity_version',
    eng
  );
  const { optimisticValues, selectedCount, findStableValue, applyOptimistic } =
    useOptimisticFacetSelection(stableValues);

  const getLabel = (start: number, end: number) => {
    const range = VERSION_RANGES.find(
      (r) => r.start === start && r.end === end
    );
    if (!range) return `${start}-${end}`;
    const entry = searchLabelsMicrocopy?.find((m) => m.key === range.key);
    return entry?.value ?? range.label;
  };

  const handleClear = useCallback(() => {
    controller.deselectAll();
  }, [controller]);

  const isDark = variant === 'mobile';

  const content = (
    <div className="flex flex-col">
      {optimisticValues.map((facetValue) => (
        <CheckboxWithLabel
          key={`unity-version-${facetValue.start}-${facetValue.end}`}
          id={`unity-version-${facetValue.start}-${facetValue.end}`}
          checked={facetValue.state === 'selected'}
          disabled={
            facetValue.numberOfResults === 0 && facetValue.state !== 'selected'
          }
          onCheckedChange={() => {
            const stableValue = findStableValue(facetValue);
            if (!stableValue) return;
            applyOptimistic([stableValue], () =>
              controller.toggleSelect(stableValue)
            );
            controller.toggleSelect(stableValue);
          }}
          count={facetValue.numberOfResults}
          size="sm"
          isDarkTheme={isDark}
          microcopy={{ label: getLabel(facetValue.start, facetValue.end) }}
        />
      ))}
    </div>
  );

  if (variant === 'horizontal') {
    return (
      <FacetDropdown
        label={versionLabel}
        selectedCount={selectedCount}
        clearLabel={clearLabel}
        onClear={handleClear}
        className={className}
      >
        {content}
      </FacetDropdown>
    );
  }

  return (
    <FacetAccordion
      title={versionLabel}
      variant={variant}
      className={className}
      onClear={handleClear}
      clearLabel={clearLabel}
      showClear={variant === 'drawer' && selectedCount > 0}
      defaultOpen={variant !== 'drawer'}
      selectedCount={selectedCount}
      isOpen={isOpen}
      onToggle={onToggle}
    >
      {content}
    </FacetAccordion>
  );
};
